/**
 * AssetSessionPanel
 *
 * Shows per-asset-class daily session status: P&L vs target, progress bar,
 * active/halted state, and an admin resume button.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { BarChart2, Coins, Crosshair, TrendingUp, CheckCircle2, XCircle, RefreshCw, PlayCircle } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { titanApi, ApiError } from '../../lib/api';
import { useEquitiesMode } from '../../hooks/useEquitiesMode';

const ASSET_META: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  stocks:  { label: 'Stocks',  icon: <BarChart2  className="w-4 h-4" />, color: 'text-blue-500'   },
  crypto:  { label: 'Crypto',  icon: <Coins      className="w-4 h-4" />, color: 'text-amber-500'  },
  forex:   { label: 'Forex',   icon: <Crosshair  className="w-4 h-4" />, color: 'text-emerald-500' },
  futures: { label: 'Futures', icon: <TrendingUp className="w-4 h-4" />, color: 'text-violet-500' },
};

interface AssetSession {
  is_active: boolean;
  halt_reason: string | null;
  daily_pnl_usd: number;
  daily_target_usd: number;
  daily_loss_limit_usd: number;
  progress_pct: number;
  trades_today: number;
}

interface WeeklyMandate {
  rolling_week_pnl_usd?: number;
  weekly_profit_target_usd?: number;
  weekly_max_loss_usd?: number;
  progress_pct_of_weekly_target?: number | null;
  iso_week_key?: string | null;
}

interface GlobalSession {
  daily_pnl: number;
  daily_pnl_broker?: number;
  daily_pnl_realized_stocks?: number;
  daily_target_usd: number;
  is_trading_allowed: boolean;
  status: string;
  pnl_basis_note?: string;
  weekly_mandate?: WeeklyMandate;
}

interface Props {
  compact?: boolean;
  showResume?: boolean;  // only show resume buttons for admins
}

const AssetSessionPanel: React.FC<Props> = ({ compact = false, showResume = false }) => {
  const { equities_only: equitiesOnly } = useEquitiesMode();
  const [sessions, setSessions] = useState<Record<string, AssetSession>>({});
  const [global, setGlobal] = useState<GlobalSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [resuming, setResuming] = useState<string | null>(null);

  const fetchSessions = useCallback(async () => {
    try {
      const data = await titanApi.assetSessions();
      if (data?.sessions) setSessions(data.sessions);
      if (data?.global) setGlobal(data.global);
    } catch {
      // non-fatal: controller might not be running
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
    const t = setInterval(fetchSessions, 15000);
    return () => clearInterval(t);
  }, [fetchSessions]);

  const handleResume = async (ac: string) => {
    setResuming(ac);
    try {
      await titanApi.resumeAssetSession(ac);
      await fetchSessions();
    } catch (e) {
      console.error('Resume failed:', e instanceof ApiError ? e.detail : e);
    } finally {
      setResuming(null);
    }
  };

  const fmt$ = (v: number) => v === 0 ? '$0' : v >= 0 ? `+$${v.toFixed(0)}` : `-$${Math.abs(v).toFixed(0)}`;
  const fmtTarget = (v: number) => v > 0 ? `$${v.toFixed(0)}` : '—';

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground p-3">
        <RefreshCw className="w-3 h-3 animate-spin" /> Loading sessions…
      </div>
    );
  }

  const sessionEntries = Object.entries(sessions).filter(
    ([ac]) => !equitiesOnly || ac === 'stocks',
  );
  if (sessionEntries.length === 0) return null;

  // Global progress
  const globalProgress = global && global.daily_target_usd > 0
    ? Math.min(100, (global.daily_pnl / global.daily_target_usd) * 100)
    : 0;

  return (
    <div className="space-y-3">
      {/* Weekly mandate (Sprint 10) */}
      {global?.weekly_mandate &&
        (global.weekly_mandate.weekly_profit_target_usd ?? 0) > 0 && (
        <Card className="border border-border">
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold">Rolling week (ISO)</span>
              <span className="text-[10px] font-mono text-muted-foreground">
                {global.weekly_mandate.iso_week_key ?? '—'}
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className={`text-sm font-mono font-bold ${
                (global.weekly_mandate.rolling_week_pnl_usd ?? 0) >= 0 ? 'text-emerald-600' : 'text-rose-500'
              }`}>
                {fmt$(global.weekly_mandate.rolling_week_pnl_usd ?? 0)}
              </span>
              <span className="text-[11px] text-muted-foreground">
                vs profit lock {fmtTarget(global.weekly_mandate.weekly_profit_target_usd ?? 0)}
              </span>
            </div>
            {global.weekly_mandate.progress_pct_of_weekly_target != null && (
              <Progress
                value={Math.min(100, Math.max(0, global.weekly_mandate.progress_pct_of_weekly_target))}
                className="h-2 mt-2 [&>div]:bg-violet-500"
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* Global goal bar */}
      {global && global.daily_target_usd > 0 && (
        <Card className={`border ${global.is_trading_allowed ? 'border-border' : 'border-emerald-500/40 bg-emerald-500/5'}`}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold">Daily Portfolio Goal</span>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-mono font-bold ${global.daily_pnl >= 0 ? 'text-emerald-600' : 'text-rose-500'}`}>
                  {fmt$(global.daily_pnl)} / {fmtTarget(global.daily_target_usd)}
                </span>
                {!global.is_trading_allowed && global.status === 'emergency' && (
                  <Badge className="bg-emerald-500/10 text-emerald-600 text-[10px] px-1.5 py-0">
                    <CheckCircle2 className="w-3 h-3 mr-1 inline" /> Goal Reached
                  </Badge>
                )}
              </div>
            </div>
            <Progress
              value={Math.max(0, globalProgress)}
              className={`h-2 ${global.daily_pnl < 0 ? '[&>div]:bg-rose-500' : '[&>div]:bg-emerald-500'}`}
            />
            <div className="text-[10px] text-muted-foreground mt-1 space-y-0.5">
              <p>{globalProgress.toFixed(1)}% of daily target — stops automatically when reached</p>
              {global.daily_pnl_realized_stocks != null
                && Math.abs((global.daily_pnl_broker ?? global.daily_pnl) - global.daily_pnl_realized_stocks) > 1 && (
                <p>
                  Stocks realized (Titan exits): {fmt$(global.daily_pnl_realized_stocks)} · Broker day:{' '}
                  {fmt$(global.daily_pnl_broker ?? global.daily_pnl)}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Per-asset cards */}
      <div className={`grid gap-2 ${compact ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
        {sessionEntries.map(([ac, session]) => {
          const meta = ASSET_META[ac] || { label: ac, icon: null, color: 'text-foreground' };
          const lossProgress = session.daily_loss_limit_usd > 0
            ? Math.min(100, (Math.abs(Math.min(0, session.daily_pnl_usd)) / session.daily_loss_limit_usd) * 100)
            : 0;
          const gainProgress = session.daily_target_usd > 0
            ? Math.min(100, (Math.max(0, session.daily_pnl_usd) / session.daily_target_usd) * 100)
            : 0;

          return (
            <Card
              key={ac}
              className={`relative overflow-hidden transition-all ${
                !session.is_active
                  ? session.halt_reason?.includes('profit target') || session.halt_reason?.includes('goal')
                    ? 'border-emerald-500/30 bg-emerald-500/5'
                    : session.halt_reason?.includes('Zero allocation')
                    ? 'opacity-50 border-dashed'
                    : 'border-rose-500/30 bg-rose-500/5'
                  : ''
              }`}
            >
              <CardContent className="p-3 space-y-2">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className={`flex items-center gap-1.5 text-sm font-semibold ${meta.color}`}>
                    {meta.icon}
                    {meta.label}
                  </div>
                  {session.is_active ? (
                    <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      Active
                    </span>
                  ) : session.halt_reason?.includes('Zero allocation') ? (
                    <span className="text-[10px] text-muted-foreground">Not allocated</span>
                  ) : (
                    <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${
                      session.halt_reason?.includes('profit') ? 'text-emerald-600' : 'text-rose-500'
                    }`}>
                      <XCircle className="w-3 h-3" />
                      {session.halt_reason?.includes('profit') ? 'Goal Hit' : 'Halted'}
                    </span>
                  )}
                </div>

                {/* P&L */}
                <div className="flex items-baseline justify-between">
                  <span className={`text-lg font-bold font-mono ${
                    session.daily_pnl_usd > 0 ? 'text-emerald-600' : session.daily_pnl_usd < 0 ? 'text-rose-500' : 'text-muted-foreground'
                  }`}>
                    {fmt$(session.daily_pnl_usd)}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    {session.trades_today} trade{session.trades_today !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* Progress bars */}
                {session.daily_target_usd > 0 && (
                  <div>
                    <div className="flex justify-between text-[10px] text-muted-foreground mb-0.5">
                      <span>Target: {fmtTarget(session.daily_target_usd)}</span>
                      <span>{gainProgress.toFixed(0)}%</span>
                    </div>
                    <Progress value={gainProgress} className="h-1.5 [&>div]:bg-emerald-500" />
                  </div>
                )}
                {session.daily_pnl_usd < 0 && session.daily_loss_limit_usd > 0 && (
                  <div>
                    <div className="flex justify-between text-[10px] text-rose-400 mb-0.5">
                      <span>Loss limit: -{fmtTarget(session.daily_loss_limit_usd)}</span>
                      <span>{lossProgress.toFixed(0)}%</span>
                    </div>
                    <Progress value={lossProgress} className="h-1.5 [&>div]:bg-rose-500" />
                  </div>
                )}

                {/* Resume button (admin) */}
                {showResume && !session.is_active && !session.halt_reason?.includes('Zero allocation') && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full h-6 text-[10px] gap-1 mt-1"
                    onClick={() => handleResume(ac)}
                    disabled={resuming === ac}
                  >
                    {resuming === ac
                      ? <RefreshCw className="w-3 h-3 animate-spin" />
                      : <PlayCircle className="w-3 h-3" />}
                    Resume {meta.label}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default AssetSessionPanel;
